import React, { useState, useEffect, useCallback } from 'react';
import { devLog, devWarn, devError } from '../../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import ResponsiveView from '../../../layout/ResponsiveView';
import ImageView from '../../../components/ImageView';
import LiveBadge from '../../../customComponents/LiveBadge';
import { useLang } from '../../../context/LangContext';
import { getProcessingAuctions } from '../../../lib/api/user/userApi';
import { formatLiveTime, formatAuctionPeriod } from '../../../utils/dateUtils';
import { isShoppingMode } from '../../../config/appMode';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const SectionContainer = styled.div`
  width: 100%;
  padding: 24px 0;
  background-color: #f3f3f3;
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  margin: 0;
`;

const ViewAllLink = styled.button`
  font-size: 15px;
  font-weight: 500;
  color: #666666;
  text-decoration: underline;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
`;

// 모바일용 스와이퍼 컨테이너
const MobileSwiperContainer = styled.div`
  width: 100%;
  
  .swiper-slide {
    display: flex;
    justify-content: center;
    transition: opacity 0.3s ease;
  }
  
  /* 비활성 슬라이드에 반투명 효과 적용 */
  .swiper-slide:not(.swiper-slide-active) {
    opacity: 0.5;
  }
  
  /* 중앙 슬라이드는 완전 불투명 */
  .swiper-slide-active {
    opacity: 1;
  }
`;

// 데스크톱용 스와이퍼 컨테이너 (네비게이션 버튼 포함)
const DesktopSwiperContainer = styled.div`
  width: 100%;
  position: relative;
  
  .swiper {
    padding: 0 60px; /* 네비게이션 버튼 공간 확보 */
  }
  
  /* 네비게이션 버튼 스타일 */
  .swiper-button-next,
  .swiper-button-prev {
    width: 44px;
    height: 44px;
    margin-top: -22px;
    background: white;
    border: 2px solid #e5e5e5;
    border-radius: 50%;
    color: #666;
    font-size: 16px;
    font-weight: bold;
    transition: all 0.2s ease;
    
    &:after {
      font-size: 16px;
      font-weight: bold;
    }
    
    &:hover {
      background: #f8f9fa;
      border-color: #007bff;
      color: #007bff;
      transform: scale(1.05);
    }
    
    &.swiper-button-disabled {
      opacity: 0.4;
      cursor: not-allowed;
      
      &:hover {
        background: white;
        border-color: #e5e5e5;
        color: #666;
        transform: none;
      }
    }
  }
  
  .swiper-button-prev {
    left: 10px;
  }
  
  .swiper-button-next {
    right: 10px;
  }
`;

// 모바일용 카드 (화면의 85% 너비)
const MobileCard = styled.div`
  width: 85vw;
  height: 300px;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  position: relative;
`;

// 데스크톱용 카드 (312*462)
const DesktopCard = styled.div`
  width: 312px;
  height: 462px;
  background-color: #ffffff;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  position: relative;
`;

// 벳지 컨테이너
const BadgeContainer = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  width: auto;
`;

// 이미지 스와이퍼 컨테이너 (모바일용)
const MobileImageSwiperContainer = styled.div`
  width: 120px;
  height: 120px;
  align-self: center;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  .swiper {
    width: 100% !important;
    height: 100% !important;
    border-radius: 8px;
    overflow: hidden !important;
  }
  
  .swiper-wrapper {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
  }
  
  .swiper-slide {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    flex-shrink: 0 !important;
    min-width: 100% !important;
    max-width: 100% !important;
  }
  
  .swiper-pagination {
    bottom: 8px !important;
    z-index: 10;
    
    .swiper-pagination-bullet {
      width: 6px;
      height: 6px;
      background: #ccc !important;
      opacity: 1 !important;
      margin: 0 3px !important;
      border: 1px solid white !important;
    }
    
    .swiper-pagination-bullet-active {
      background: #007bff !important;
      border: 1px solid white !important;
    }
  }
`;

// 이미지 스와이퍼 컨테이너 (데스크톱용)
const DesktopImageSwiperContainer = styled.div`
  width: 320px;
  height: 320px;
  align-self: center;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  
  .swiper {
    width: 320px !important;
    height: 290px !important;
    border-radius: 8px;
    overflow: visible !important;
  }
  
  .swiper-wrapper {
    width: 320px !important;
    height: 290px !important;
    display: flex !important;
  }
  
  .swiper-slide {
    width: 280px !important;
    height: 290px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    flex-shrink: 0 !important;
    transition: opacity 0.3s ease !important;
  }
  
  /* 비활성 슬라이드에 반투명 효과 적용 */
  .swiper-slide:not(.swiper-slide-active) {
    opacity: 0.4;
  }
  
  /* 중앙 슬라이드는 완전 불투명 */
  .swiper-slide-active {
    opacity: 1;
  }
  
  .swiper-pagination {
    bottom: 0px !important;
    z-index: 100 !important;
    position: absolute !important;
    
    .swiper-pagination-bullet {
      width: 8px;
      height: 8px;
      background: #ccc !important;
      opacity: 1 !important;
      margin: 0 4px !important;
      border: 2px solid white !important;
    }
    
    .swiper-pagination-bullet-active {
      background: #007bff !important;
      border: 2px solid white !important;
    }
  }
`;

// 옥션 정보 컨테이너 (하단에 고정)
const AuctionInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
`;

// 경매사 섹션
const AuctionHouseSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  width: 100%;
`;

const AuctionHouseName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #666;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const AuctionHouseButton = styled.span`
  font-size: 14px;
  color: #666;
  flex-shrink: 0;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

// 경매명
const AuctionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #69b5b0;
  margin: 0;
  line-height: 1.3;
  text-align: left;
  align-self: flex-start;
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 768px) {
    font-size: 20px;
    font-weight: 700;
  }
`;

// 날짜 정보
const DateInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  width: 100%;
  
  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const DateLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #666;

  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

const DateValue = styled.span`
  font-size: 12px;
  color: #666;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

// 다국어 텍스트 객체
const liveSectionTexts = {
  ko: {
    title: isShoppingMode() ? '판매중인 상품' : '진행중인 경매',
    viewAll: '전체보기',
    photoSubmissionDate: isShoppingMode() ? '판매시작' : '사진입찰 일시',
    auctionDate: isShoppingMode() ? '판매종료' : '경매 기간',
    preparing: isShoppingMode() ? '판매 준비중입니다' : '경매 준비중입니다'
  },
  en: {
    title: isShoppingMode() ? 'Products On Sale' : 'Ongoing Auctions',
    viewAll: 'View All',
    photoSubmissionDate: isShoppingMode() ? 'Sale Start' : 'Photo Bid Date',
    auctionDate: isShoppingMode() ? 'Sale End' : 'Auction Period',
    preparing: isShoppingMode() ? 'Preparing for sale' : 'Preparing for auction'
  }
};

// Props 인터페이스 정의
interface LiveSectionProps {
  onLoadingChange?: (isLoading: boolean) => void;
}

const LiveSection: React.FC<LiveSectionProps> = ({ onLoadingChange }) => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const texts = liveSectionTexts[lang];
  
  // 상태 관리
  const [auctions, setAuctions] = useState<any[]>([]); // 임시로 any 타입 사용
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 카드 클릭 핸들러 (경매 전체로 이동하는 로직)
  const handleCardClick = (auction: any) => {
    if (auction && auction.index) {
      // 경매의 모든 lots를 AuctionItem 형태로 변환
      const auctionItems = auction.lots?.map((lot: any) => ({
        id: lot.index,
        imageUrl: lot.images?.[0] ? getProfileImageSrc(lot.images[0]) : '',
        auctionHouse: auction.auctioneer?.name || '경매사',
        auction: auction.name,
        lotName: lot.name,
        startingPrice: lot.startPrice ? lot.startPrice.toLocaleString() : '0',
        startingPriceUsd: lot.startPriceUsd,
        exchangeRate: lot.exchangeRate || 1300,
        liveTime: auction.auctionStartAt,
        liveEndTime: auction.auctionEndAt,
        isLive: new Date() >= new Date(auction.auctionStartAt) && new Date() <= new Date(auction.auctionEndAt),
        photoSubmissionDate: '',
        auctionStartDate: '',
        auctionEndDate: '',
        isFavorite: lot.likeCount > 0,
        isEnded: new Date() > new Date(auction.auctionEndAt),
        likedUserIndexes: lot.likedUserIndexes || [],
        apiData: lot
      })) || [];

      // 첫 번째 lot으로 이동 (만약 lots가 있다면)
      if (auctionItems.length > 0) {
        navigate(`/lot-detail/${auction.index}`, { 
          state: { 
            auction: auction.name,
            auctionHouse: auction.auctioneer?.name || '경매사',
            items: auctionItems,
            exchangeRate: auctionItems[0].exchangeRate,
            selectedItem: auctionItems[0]
          } 
        });
      } else {
        // lots가 없는 경우 경매 상세 페이지로 이동
        navigate(`/auction-detail/${auction.index}`);
      }
    }
  };

  // API 호출 함수
  const fetchAuctions = useCallback(async () => {
    try {
      setLoading(true);
      onLoadingChange?.(true);
      setError(null);
      const response = await getProcessingAuctions({ page: 1 });
      
      // API 응답이 res.json 형태로 올 때 처리
      let auctionsData = [];
      
      // response 자체가 배열인 경우 (직접 경매 데이터)
      if (Array.isArray(response)) {
        auctionsData = response;
      }
      // response.data가 배열인 경우
      else if (Array.isArray(response?.data)) {
        auctionsData = response.data;
      }
      // response.data.data.auctions가 있는 경우 (기존 구조)
      else if ((response as any)?.data?.data?.auctions) {
        auctionsData = (response as any).data.data.auctions;
      }
      // response.data.auctions가 있는 경우
      else if (response?.data?.auctions) {
        auctionsData = response.data.auctions;
      }
      
      setAuctions(auctionsData);
    } catch (err) {
      devError('Failed to fetch processing auctions:', err);
      setError('경매 정보를 불러오는데 실패했습니다.');
      setAuctions([]);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [onLoadingChange]);

  // API 호출
  useEffect(() => {
    fetchAuctions();
  }, [fetchAuctions]);

  // 전체보기 클릭 핸들러
  const handleViewAllClick = () => {
    navigate('/ongoing-auctions');
  };

  // 이미지 URL 처리 함수 (RecommendationSection과 동일)
  const getProfileImageSrc = (profileImageUrl?: string | null) => {
    if (!profileImageUrl || profileImageUrl.trim() === "") {
      return "/common/nodata.svg";
    }
    
    // 이미 완전한 URL인 경우 (http, https로 시작하거나 blob: URL)
    if (profileImageUrl.startsWith("http") || profileImageUrl.startsWith("blob:")) {
      return profileImageUrl;
    }
    
    // 절대 경로인 경우 (/로 시작)
    if (profileImageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${profileImageUrl}`;
    }
    
    // 단순 파일명인 경우 (서버에서 받은 파일명)
    return `${process.env.REACT_APP_API_HOST}/${profileImageUrl}`;
  };

  // 경매의 모든 이미지 URL을 가져오는 함수 (실제 이미지만, 최대 3장 제한)
  const getAllImageUrls = (auction: any): string[] => {
    const imageUrls: string[] = [];
    
    // lots를 순회하면서 모든 이미지를 수집 (최대 3장까지만)
    if (auction.lots && Array.isArray(auction.lots)) {
      auction.lots.forEach((lot: any) => {
        if (imageUrls.length >= 3) return; // 이미 3장에 도달했으면 중단
        
        if (lot.images && Array.isArray(lot.images)) {
          lot.images.forEach((imagePath: string) => {
            if (imageUrls.length >= 3) return; // 이미 3장에 도달했으면 중단
            
            if (imagePath && imagePath.trim() !== "") {
              imageUrls.push(getProfileImageSrc(imagePath));
            }
          });
        }
      });
    }
    
    return imageUrls;
  };

  // 첫 번째 이미지 또는 기본 이미지를 가져오는 함수
  const getFirstImageUrl = (auction: any): string => {
    const imageUrls = getAllImageUrls(auction);
    return imageUrls.length > 0 ? imageUrls[0] : "/common/no_image.svg";
  };



  // 로딩 중이거나 데이터가 없을 때의 처리
  if (loading) {
    return (
      <SectionContainer>
        <TitleContainer>
          <SectionTitle>{texts.title}</SectionTitle>
          <ViewAllLink onClick={handleViewAllClick}>{texts.viewAll}</ViewAllLink>
        </TitleContainer>
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          로딩 중...
        </div>
      </SectionContainer>
    );
  }

  if (error || !auctions || auctions.length === 0) {
    return (
      <SectionContainer>
        <TitleContainer>
          <SectionTitle>{texts.title}</SectionTitle>
        </TitleContainer>
        <div style={{ 
          padding: '40px', 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <ImageView
            imageUrl="/common/nodata.svg"
            width={107}
            height={107}
            alt="No data"
          />
          <div style={{ 
            marginTop: '20px',
            color: '#949494',
            fontSize: '14px',
            fontWeight: 500
          }}>
            {texts.preparing}
          </div>
        </div>
      </SectionContainer>
    );
  }



  // 데스크톱 레이아웃
  const DesktopLayout = (
    <SectionContainer>
      <TitleContainer>
        <SectionTitle>{texts.title}</SectionTitle>
        <ViewAllLink onClick={handleViewAllClick}>{texts.viewAll}</ViewAllLink>
      </TitleContainer>
      
      <DesktopSwiperContainer>
        <Swiper
          modules={[Navigation]}
          slidesPerView={3}
          spaceBetween={20}
          navigation={auctions && auctions.length > 3}
          loop={false}
        >
          {auctions?.map((auction) => (
            <SwiperSlide key={`desktop-auction-${auction.index}`}>
              <DesktopCard onClick={() => handleCardClick(auction)}>
                {!isShoppingMode() && (
                  <BadgeContainer>
                    <LiveBadge liveTime={formatLiveTime(auction.auctionStartAt, lang, auction.auctionEndAt)} isMobile={false} />
                  </BadgeContainer>
                )}
                
                <DesktopImageSwiperContainer
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                >
                  {(() => {
                    const imageUrls = getAllImageUrls(auction);
                    if (imageUrls.length < 1) {
                      // 이미지가 1개 이하면 기본 ImageView 사용 (ImageView가 기본 이미지 처리)
                      return (
                        <ImageView
                          width={280}
                          height={280}
                          borderRadius={8}
                          alt={auction.name}
                          objectFit="contain"
                        />
                      );
                    } 
                    else {
                      // 이미지가 2개 이상이면 스와이퍼 사용
                      return (
                        <Swiper
                          modules={[Pagination]}
                          slidesPerView={1.15}
                          slidesPerGroup={1}
                          spaceBetween={10}
                          pagination={{ 
                            clickable: true,
                            dynamicBullets: false,
                            hideOnClick: false
                          }}
                          loop={imageUrls.length > 3}
                          centeredSlides={true}
                          allowTouchMove={true}
                          touchStartPreventDefault={false}
                          grabCursor={true}
                          watchOverflow={false}
                          style={{ 
                            width: '320px', 
                            height: '290px',
                            maxWidth: '320px',
                            maxHeight: '290px',
                            overflow: 'visible',
                            paddingBottom: '30px'
                          }}
                        >
                          {imageUrls.map((imageUrl, index) => (
                            <SwiperSlide key={`desktop-${auction.index}-${index}`} style={{ 
                              width: '280px', 
                              height: '290px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <ImageView
                                imageUrl={imageUrl}
                                width={280}
                                height={260}
                                borderRadius={8}
                                alt={`${auction.name} ${index + 1}`}
                                objectFit="contain"
                              />
                            </SwiperSlide>
                          ))}
                        </Swiper>
                      );
                    }
                  })()}
                </DesktopImageSwiperContainer>
                
                <AuctionInfoContainer>
                  <AuctionHouseSection>
                    <AuctionHouseName>{auction.auctioneer?.name || '경매사'}</AuctionHouseName>
                    <AuctionHouseButton className="arrow-icon">›</AuctionHouseButton>
                  </AuctionHouseSection>
                  
                  <AuctionTitle>{auction.name}</AuctionTitle>
                  
                  {!isShoppingMode() && (
                    <>
                      <DateInfo>
                        <DateLabel>{texts.photoSubmissionDate}</DateLabel>
                        <DateValue>{formatAuctionPeriod(auction.preBidStartAt, auction.preBidEndAt)}</DateValue>
                      </DateInfo>
                      
                      <DateInfo>
                        <DateLabel>{texts.auctionDate}</DateLabel>
                        <DateValue>{formatAuctionPeriod(auction.auctionStartAt, auction.auctionEndAt)}</DateValue>
                      </DateInfo>
                    </>
                  )}
                </AuctionInfoContainer>
              </DesktopCard>
            </SwiperSlide>
          ))}
        </Swiper>
      </DesktopSwiperContainer>
    </SectionContainer>
  );

  // 모바일 레이아웃
  const MobileLayout = (
    <SectionContainer>
      <TitleContainer>
        <SectionTitle>{texts.title}</SectionTitle>
        <ViewAllLink onClick={handleViewAllClick}>{texts.viewAll}</ViewAllLink>
      </TitleContainer>
      
      <MobileSwiperContainer>
        <Swiper
          centeredSlides={true}
          slidesPerView={1.2}
          spaceBetween={20}
          loop={auctions && auctions.length > 2}
        >
          {auctions?.map((auction) => (
            <SwiperSlide key={`mobile-auction-${auction.index}`}>
              <MobileCard onClick={() => handleCardClick(auction)}>
                {!isShoppingMode() && (
                  <BadgeContainer>
                    <LiveBadge liveTime={formatLiveTime(auction.auctionStartAt, lang, auction.auctionEndAt)} isMobile={true} />
                  </BadgeContainer>
                )}
                
                <MobileImageSwiperContainer
                  onTouchStart={(e: React.TouchEvent) => e.stopPropagation()}
                  onTouchMove={(e: React.TouchEvent) => e.stopPropagation()}
                  onTouchEnd={(e: React.TouchEvent) => e.stopPropagation()}
                >
                  {(() => {
                    const imageUrls = getAllImageUrls(auction);
                    if (imageUrls.length <= 1) {
                      // 이미지가 1개 이하면 기본 ImageView 사용 (ImageView가 기본 이미지 처리)
                      return (
                        <ImageView
                          imageUrl={getFirstImageUrl(auction)}
                          width={120}
                          height={120}
                          borderRadius={8}
                          alt={auction.name}
                          objectFit="contain"
                        />
                      );
                    } else {
                      // 이미지가 2개 이상이면 스와이퍼 사용
                      return (
                        <Swiper
                          modules={[Pagination]}
                          slidesPerView={1}
                          slidesPerGroup={1}
                          spaceBetween={0}
                          pagination={{ clickable: true }}
                          loop={imageUrls.length > 1}
                          centeredSlides={true}
                          allowTouchMove={true}
                          touchStartPreventDefault={false}
                          grabCursor={true}
                          style={{ 
                            width: '120px', 
                            height: '120px',
                            maxWidth: '120px',
                            maxHeight: '120px'
                          }}
                        >
                          {imageUrls.map((imageUrl, index) => (
                            <SwiperSlide key={`mobile-${auction.index}-${index}`} style={{ 
                              width: '120px !important', 
                              height: '120px !important',
                              maxWidth: '120px',
                              maxHeight: '120px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <ImageView
                                imageUrl={imageUrl}
                                width={120}
                                height={120}
                                borderRadius={8}
                                alt={`${auction.name} ${index + 1}`}
                                objectFit="contain"
                              />
                            </SwiperSlide>
                          ))}
                        </Swiper>
                      );
                    }
                  })()}
                </MobileImageSwiperContainer>
                
                <AuctionInfoContainer>
                  <AuctionHouseSection>
                    <AuctionHouseName>{auction.auctioneer?.name || '경매사'}</AuctionHouseName>
                    <AuctionHouseButton className="arrow-icon">›</AuctionHouseButton>
                  </AuctionHouseSection>
                  
                  <AuctionTitle>{auction.name}</AuctionTitle>
                  
                  {!isShoppingMode() && (
                    <>
                      <DateInfo>
                        <DateLabel>{texts.photoSubmissionDate}</DateLabel>
                        <DateValue>{formatAuctionPeriod(auction.preBidStartAt, auction.preBidEndAt)}</DateValue>
                      </DateInfo>
                      
                      <DateInfo>
                        <DateLabel>{texts.auctionDate}</DateLabel>
                        <DateValue>{formatAuctionPeriod(auction.auctionStartAt, auction.auctionEndAt)}</DateValue>
                      </DateInfo>
                    </>
                  )}
                </AuctionInfoContainer>
              </MobileCard>
            </SwiperSlide>
          ))}
        </Swiper>
      </MobileSwiperContainer>
    </SectionContainer>
  );

  return (
    <ResponsiveView
      mobileView={MobileLayout}
      desktopView={DesktopLayout}
    />
  );
};

export default LiveSection;

//todo : 브레이크 포인트 useDevice 변경 필요
